import { useEffect, useState } from 'react';
import { AppBannerOs, APP_BANNER_DISMISS_COOKIE, APP_BANNER_DISMISS_DAYS } from '@/libs/openAppBanner';

const setDismissCookie = () => {
  document.cookie = `${APP_BANNER_DISMISS_COOKIE}=1; max-age=${APP_BANNER_DISMISS_DAYS * 86400}; path=/; SameSite=Lax`;
};

/**
 * Thanh "Mở ứng dụng An Thư" trên web mobile (giống Shopee/Facebook).
 *
 * - Android: intent:// → mở app nếu đã cài, chưa cài thì tự sang Google Play.
 * - iOS: sang App Store (nếu đã cài, App Store hiện nút "Mở").
 *   Riêng Safari iOS đã có banner hệ thống qua thẻ meta apple-itunes-app (_app.tsx),
 *   nên thanh này KHÔNG hiện trên Safari để khỏi hiện 2 banner.
 * - Bấm X: ẩn 7 ngày (cookie để server biết + localStorage).
 */
const ANDROID_PACKAGE = 'com.anthu.diamond';
const GOOGLE_STORE_URL =
  process.env.GOOGLE_STORE_URL || `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE}`;
const APPLE_STORE_URL = process.env.APPLE_STORE_URL || 'https://apps.apple.com/vn/app/anthu/id1613308085';
const DISMISS_KEY = 'open_app_banner_dismissed_at';

type Props = {
  /** quyết định từ server (withAuthServerSideProps) → HTML đã có sẵn thanh, không nhảy layout (CLS) */
  initialOs?: AppBannerOs;
};

const OpenAppBanner = ({ initialOs = null }: Props) => {
  const [os, setOs] = useState<AppBannerOs>(initialOs);

  useEffect(() => {
    // người đã đóng bằng localStorage (bản cũ, chưa có cookie) → ẩn và đặt cookie để lần sau server biết
    try {
      const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
      if (at && Date.now() - at < APP_BANNER_DISMISS_DAYS * 86400000) {
        setDismissCookie();
        setOs(null);
      }
    } catch (_) {}
  }, []);

  if (!os) return null;

  const openHref =
    os === 'android'
      ? `intent://open#Intent;package=${ANDROID_PACKAGE};S.browser_fallback_url=${encodeURIComponent(
          GOOGLE_STORE_URL
        )};end`
      : APPLE_STORE_URL;

  const dismiss = () => {
    setDismissCookie();
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch (_) {}
    setOs(null);
  };

  return (
    <div className="md:tw-hidden tw-flex tw-items-center tw-gap-3 tw-px-3 tw-py-2 tw-bg-white tw-border-b tw-border-gray-200">
      <button aria-label="Đóng" onClick={dismiss} className="tw-text-gray-500 tw-text-lg tw-leading-none tw-px-1">
        ×
      </button>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src="/logo-bg.png" alt="An Thư" className="tw-w-9 tw-h-9 tw-rounded-lg tw-object-contain tw-border tw-border-gray-200" />
      <div className="tw-flex-1 tw-min-w-0">
        <div className="tw-text-sm tw-font-semibold tw-text-primary-main tw-truncate">App An Thư Kim Cương</div>
        <div className="tw-text-xs tw-text-gray-500 tw-truncate">Ưu đãi riêng · Xem live</div>
      </div>
      <a
        href={openHref}
        className="tw-shrink-0 tw-bg-primary-main !tw-text-white tw-text-sm tw-font-semibold tw-rounded-full tw-px-4 tw-py-1.5 tw-no-underline"
      >
        Mở app
      </a>
    </div>
  );
};

export default OpenAppBanner;
